import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Menu {
  id: number;
  title: string;
  restaurant?: {
    name: string;
  };
}

const CreateUser: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'restaurant_owner',
    menuIds: [] as string[]
  });

  const [menus, setMenus] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMenus, setLoadingMenus] = useState(false);

  // Fetch available menus
  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    setLoadingMenus(true);
    try {
      const token = localStorage.getItem('token');
      // Get all menus without pagination
      const response = await axios.get('/menu/all', {
        params: { limit: 1000 }, // Get up to 1000 menus
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Extract menus from the response
        const menusData = response.data.data;
        if (menusData.menus) {
          setMenus(menusData.menus);
        } else {
          setMenus(menusData || []);
        }
      } else {
        toast.error('Failed to load menus');
      }
    } catch (error: any) {
      console.error('Error fetching menus:', error);
      toast.error('Failed to load menus');
    } finally {
      setLoadingMenus(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.password) {
      toast.error(t('create_user.messages.required_fields'));
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare the user data
      const userData = {
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
        restaurantId: null
      };

      const response = await axios.post(
        '/users',
        userData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Assign menus to the newly created user
      if (form.menuIds && form.menuIds.length > 0) {
        for (const menuId of form.menuIds) {
          try {
            await axios.post(
              '/users/assign-menu',
              {
                userId: response.data.data.id,
                menuId: parseInt(menuId, 10)
              },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );
          } catch (err) {
            console.error(`Failed to assign menu ${menuId}:`, err);
            // Optionally notify but continue with others
          }
        }
      }

      toast.success(t('create_user.messages.create_success'));
      navigate('/users');
    } catch (error: any) {
      console.error('Create user error:', error);
      toast.error(error.response?.data?.error || t('create_user.messages.create_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to="/users"
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-[#0A0C0B]">{t('create_user.title')}</h1>
          <p className="text-[#687d76] mt-1">{t('create_user.subtitle')}</p>
        </div>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white border border-gray-200 rounded-xl p-6 space-y-5"
      >
        {/* Name */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('create_user.form.name')}</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#7BD5B5]"
            placeholder={t('create_user.form.name_placeholder')}
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('create_user.form.email')}</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#7BD5B5]"
            placeholder={t('create_user.form.email_placeholder')}
          />
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('create_user.form.password')}</label>
          <input
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#7BD5B5]"
            placeholder={t('create_user.form.password_placeholder')}
          />
        </div>

        {/* Role */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('create_user.form.role')}</label>
          <select
            name="role"
            value={form.role}
            onChange={handleChange}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#7BD5B5]"
          >
            <option value="admin">{t('user_management.roles.admin')}</option>
            <option value="agency">{t('user_management.roles.agency')}</option>
            <option value="restaurant_owner">{t('user_management.roles.restaurant_owner')}</option>
            <option value="editor">{t('user_management.roles.editor')}</option>
            <option value="user">{t('user_management.roles.user')}</option>
          </select>
        </div>

        {/* Assign Menus (optional) */}
        <div>
          <label className="block text-sm font-medium mb-1">
            {t('create_user.form.assign_menus') || 'Assign Menus'}
          </label>
          <select
            multiple
            name="menuIds"
            value={form.menuIds}
            onChange={(e) => {
              const options = e.target.options;
              const value = [];
              for (let i = 0, l = options.length; i < l; i++) {
                if (options[i].selected) {
                  value.push(options[i].value);
                }
              }
              setForm({ ...form, menuIds: value });
            }}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#7BD5B5] min-h-[120px]"
          >
            {menus.map((menu) => (
              <option key={menu.id} value={menu.id}>
                {menu.id} - {menu.title}{menu.restaurant?.name ? ` (${menu.restaurant.name})` : ''}
              </option>
            ))}
          </select>
          {loadingMenus && (
            <div className="mt-2 flex items-center text-sm text-[#687d76]">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t('create_user.form.loading_menus') || 'Loading menus...'}
            </div>
          )}
          <p className="text-xs text-[#687d76] mt-1">
            {t('create_user.form.assign_menus_help') || 'Hold Ctrl/Cmd to select multiple menus.'}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Link
            to="/users"
            className="px-4 py-3 rounded-lg border border-gray-300 hover:bg-gray-100"
          >
            {t('create_user.buttons.cancel')}
          </Link>

          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 bg-[#0A0C0B] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#1A1C1B] disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('create_user.buttons.create_user')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateUser;
